import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Briefcase,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Inbox,
  LayoutGrid,
  MessageSquareText,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  UserRound,
  UsersRound,
  X,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import CaseTypeBadge, { caseIssueTypeLabel, normaliseCaseIssueType } from '../components/CaseTypeBadge';
import { EmptyState, NameCard, Reference, Status } from '../components/UI';
import { api, dateTime, money } from '../lib/api';

const readable=value=>String(value||'—').replaceAll('_',' ').replace(/\b\w/g,match=>match.toUpperCase());
const jsonRequest=(method,body)=>({method,headers:{'content-type':'application/json'},body:JSON.stringify(body)});

function dueState(value){
  if(!value)return {label:'No target',tone:'neutral'};
  const time=new Date(value).getTime();
  if(Number.isNaN(time))return {label:'No target',tone:'neutral'};
  const diff=time-Date.now();
  if(diff<0)return {label:'Overdue',tone:'danger'};
  if(diff<24*60*60*1000)return {label:'Due today',tone:'warning'};
  if(diff<3*24*60*60*1000)return {label:'Due soon',tone:'warning'};
  return {label:dateTime(value),tone:'neutral'};
}

function Metric({label,value,detail,icon:Icon,tone='blue',to}){
  void tone;
  return <div className="govuk-grid-column-one-third govuk-!-margin-bottom-4">
    <section className="govuk-summary-card">
      <div className="govuk-summary-card__title-wrapper">
        <h2 className="govuk-summary-card__title govuk-heading-m">
          <Icon size={18} aria-hidden="true" /> {label}
        </h2>
      </div>
      <div className="govuk-summary-card__content">
        <p className="govuk-heading-l govuk-!-margin-bottom-1">{value}</p>
        <p className="govuk-body-s govuk-!-margin-bottom-3">{detail}</p>
        {to&&<Link to={to} className="govuk-link">View {label.toLowerCase()} <span className="govuk-visually-hidden">in Customer Operations</span></Link>}
      </div>
    </section>
  </div>;
}

function CaseRow({row,onOpen}){
  const due=dueState(row.due_at);
  return <button className="dashboard-case-row" type="button" onClick={()=>onOpen(row.id)}>
    <div className="dashboard-case-type"><CaseTypeBadge row={row} compact/></div>
    <div className="dashboard-case-main">
      <div className="dashboard-case-title-line"><Reference>{row.case_reference||'Case'}</Reference><strong>{row.title||caseIssueTypeLabel(row)}</strong></div>
      <span>{row.customer_name||'No Customer360 customer'}{row.customer_number?' · '+row.customer_number:''}</span>
    </div>
    <span className={'dashboard-priority priority-'+String(row.priority||'normal').toLowerCase()}>{readable(row.priority||'normal')}</span>
    <span className={'dashboard-due '+due.tone}><Clock3 size={12}/>{due.label}</span>
    <Status value={row.status||'open'}/>
    <ChevronRight size={15} className="dashboard-row-arrow"/>
  </button>;
}

function CustomDashboardCard({card,index,total,onRemove,onMove}){
  const available=card.available!==false;
  return <article className={'dashboard-custom-card tone-'+(card.tone||'blue')}>
    <div className="dashboard-custom-card-top">
      <span>{card.group||'Dashboard card'}</span>
      <div className="dashboard-custom-card-controls">
        <button type="button" onClick={()=>onMove(card.id,-1)} disabled={index===0} aria-label={'Move '+card.title+' left'} title="Move left"><ChevronLeft size={14}/></button>
        <button type="button" onClick={()=>onMove(card.id,1)} disabled={index===total-1} aria-label={'Move '+card.title+' right'} title="Move right"><ChevronRight size={14}/></button>
        <button type="button" className="remove" onClick={()=>onRemove(card.id)} aria-label={'Remove '+card.title} title="Remove from dashboard"><Trash2 size={13}/></button>
      </div>
    </div>
    <strong className="dashboard-custom-card-value">{available?(card.value??0):'—'}</strong>
    <h3 className="govuk-heading-s">{card.title}</h3>
    <p className="govuk-body">{available?card.description:'This card cannot be calculated with the current CRM schema.'}</p>
    {card.target&&<Link to={card.target}>Open related cases <ArrowRight size={13}/></Link>}
  </article>;
}

function CardLibrary({catalogue,loading,query,setQuery,group,setGroup,selectedIds,onAdd,onRemove,onClose,saving}){
  const selected=new Set(selectedIds||[]);
  const cards=catalogue?.cards||[];
  const groups=catalogue?.groups||[];
  const filtered=cards.filter(card=>{
    const matchesGroup=!group||card.group===group;
    const q=query.trim().toLowerCase();
    const matchesSearch=!q||String(card.tags||card.title+' '+card.description+' '+card.group).toLowerCase().includes(q);
    return matchesGroup&&matchesSearch;
  });

  return <div className="dashboard-library-backdrop" onMouseDown={event=>{if(event.currentTarget===event.target)onClose();}}>
    <section className="dashboard-library" role="dialog" aria-modal="true" aria-label="Add cards to dashboard">
      <header>
        <div>
          <span>DASHBOARD LIBRARY</span>
          <h2 className="govuk-heading-m">Add to dashboard</h2>
          <p className="govuk-body">{catalogue?.count??'Hundreds of'} live operational cards are available. Add the ones that matter to your work.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close dashboard card library"><X size={20}/></button>
      </header>

      <div className="dashboard-library-toolbar">
        <label className="dashboard-library-search govuk-label"><Search size={16}/><input className="govuk-input" value={query} onChange={event=>setQuery(event.target.value)} placeholder="Search hundreds of cards by type, status, priority, channel or deadline…"/></label>
        <span className="dashboard-library-selected">{selected.size} / {catalogue?.maximum_selected||48} added</span>
      </div>

      <div className="dashboard-library-groups">
        <button type="button" className={!group?'active':''} onClick={()=>setGroup('')}>All</button>
        {groups.map(item=><button type="button" className={group===item?'active':''} key={item} onClick={()=>setGroup(item)}>{item}</button>)}
      </div>

      <div className="dashboard-library-results">
        {loading?<div className="dashboard-library-loading"><div className="dashboard-spinner"/><span>Loading dashboard card catalogue…</span></div>:
          filtered.length?filtered.map(card=>{
            const added=selected.has(card.id);
            return <article key={card.id} className={'dashboard-library-card tone-'+(card.tone||'blue')+(added?' added':'')}>
              <div className="dashboard-library-card-copy">
                <span>{card.group}</span>
                <h3 className="govuk-heading-s">{card.title}</h3>
                <p className="govuk-body">{card.description}</p>
              </div>
              <button type="button" className={added?'added':''} disabled={saving} onClick={()=>added?onRemove(card.id):onAdd(card.id)}>
                {added?<><CheckCircle2 size={14}/>Added</>:<><Plus size={14}/>Add</>}
              </button>
            </article>;
          }):<div className="dashboard-library-empty"><Search size={28}/><strong>No cards matched</strong><span>Try a different search or category.</span></div>}
      </div>
    </section>
  </div>;
}

export default function Dashboard(){
  const navigate=useNavigate();
  const[data,setData]=useState(null);
  const[me,setMe]=useState(null);
  const[loading,setLoading]=useState(true);
  const[refreshing,setRefreshing]=useState(false);
  const[error,setError]=useState('');
  const[layout,setLayout]=useState({card_ids:[],maximum:48});
  const[customCards,setCustomCards]=useState([]);
  const[customLoading,setCustomLoading]=useState(false);
  const[customError,setCustomError]=useState('');
  const[savingLayout,setSavingLayout]=useState(false);
  const[libraryOpen,setLibraryOpen]=useState(false);
  const[libraryLoading,setLibraryLoading]=useState(false);
  const[catalogue,setCatalogue]=useState(null);
  const[libraryQuery,setLibraryQuery]=useState('');
  const[libraryGroup,setLibraryGroup]=useState('');

  const fetchCustomCards=useCallback(async ids=>{
    const cardIds=Array.isArray(ids)?ids:[];
    if(!cardIds.length){setCustomCards([]);setCustomError('');return}
    setCustomLoading(true);setCustomError('');
    try{
      const response=await api('/api/dashboard/cards/data',jsonRequest('POST',{card_ids:cardIds}));
      setCustomCards(response.cards||[]);
    }catch(caught){
      setCustomCards([]);
      setCustomError(caught.message||'Custom dashboard cards could not be loaded.');
    }finally{
      setCustomLoading(false);
    }
  },[]);

  const load=useCallback(async({initial=false}={})=>{
    if(initial)setLoading(true);else setRefreshing(true);
    setError('');
    try{
      const[dashboard,user,layoutInfo]=await Promise.all([
        api('/api/dashboard'),
        api('/api/me'),
        api('/api/dashboard/layout'),
      ]);
      setData(dashboard);
      setMe(user);
      setLayout(layoutInfo||{card_ids:[],maximum:48});
      await fetchCustomCards(layoutInfo?.card_ids||[]);
    }catch(caught){
      setError(caught.message||'The dashboard could not be loaded.');
    }finally{
      setLoading(false);
      setRefreshing(false);
    }
  },[fetchCustomCards]);

  useEffect(()=>{load({initial:true});},[load]);

  const openLibrary=async()=>{
    setLibraryOpen(true);
    if(catalogue)return;
    setLibraryLoading(true);
    try{
      const response=await api('/api/dashboard/cards/catalog');
      setCatalogue(response);
    }catch(caught){
      setCustomError(caught.message||'The dashboard card library could not be loaded.');
    }finally{
      setLibraryLoading(false);
    }
  };

  const persistLayout=async nextIds=>{
    setSavingLayout(true);setCustomError('');
    try{
      const response=await api('/api/dashboard/layout',jsonRequest('PUT',{card_ids:nextIds}));
      setLayout(response);
      await fetchCustomCards(response.card_ids||[]);
    }catch(caught){
      setCustomError(caught.message||'The dashboard layout could not be saved.');
    }finally{
      setSavingLayout(false);
    }
  };

  const addCard=id=>{
    const current=layout.card_ids||[];
    if(current.includes(id)||current.length>=Number(layout.maximum||48))return;
    persistLayout([...current,id]);
  };

  const removeCard=id=>persistLayout((layout.card_ids||[]).filter(item=>item!==id));

  const moveCard=(id,direction)=>{
    const next=[...(layout.card_ids||[])];
    const index=next.indexOf(id);
    const target=index+direction;
    if(index<0||target<0||target>=next.length)return;
    [next[index],next[target]]=[next[target],next[index]];
    persistLayout(next);
  };

  const firstName=me?.staff?.display_name?.trim()?.split(/\s+/)?.[0]||'there';
  const m=data?.metrics||{};
  const workload=data?.caseWorkload||[];
  const maxWorkload=Math.max(1,...workload.map(item=>Number(item.count||0)));
  const visibleWarnings=(data?.warnings||[]).filter(Boolean);
  const attention=useMemo(()=>[
    {label:'Overdue',value:m.overdueCases,tone:Number(m.overdueCases)>0?'danger':'good',to:'/cases'},
    {label:'High / critical',value:m.urgentCases,tone:Number(m.urgentCases)>0?'warning':'good',to:'/cases'},
    {label:'Unassigned',value:m.unassignedCases,tone:Number(m.unassignedCases)>0?'warning':'good',to:'/cases?queue=unassigned'},
    {label:'Open complaints',value:m.openComplaints,tone:Number(m.openComplaints)>0?'neutral':'good',to:'/complaints'},
  ],[m.overdueCases,m.urgentCases,m.unassignedCases,m.openComplaints]);

  if(loading)return <div className="operations-dashboard"><div className="dashboard-loading"><div className="dashboard-spinner"/><strong>Opening Customer Operations</strong><span>Loading live cases, customers, sales and activity…</span></div></div>;
  if(error&&!data)return <div className="operations-dashboard"><EmptyState title="Dashboard unavailable" body={error}/><button className="button primary govuk-button" onClick={()=>load({initial:true})}>Try again</button></div>;

  return <div className="operations-dashboard">
    <header className="govuk-!-margin-bottom-8">
      <span className="govuk-caption-l">Sousa Murray / Customer Operations</span>
      <h1 className="govuk-heading-xl">Good afternoon, {firstName}</h1>
      <p className="govuk-body">Here's what's happening across your customer operations today.</p>
      <p className="govuk-body-s" role="status">Live data {data?.generated_at&&<>· Updated {dateTime(data.generated_at)}</>}</p>
      <div className="govuk-button-group">
        <button className="govuk-button govuk-button--secondary" type="button" onClick={openLibrary}><LayoutGrid size={16}/>Add to dashboard</button>
        <button className="govuk-button govuk-button--secondary" type="button" onClick={()=>load()} disabled={refreshing}><RefreshCw size={16}/>{refreshing?'Refreshing…':'Refresh'}</button>
        <Link className="govuk-button" to="/requests/new"><Plus size={16}/>Create work item</Link>
      </div>
    </header>

    <nav aria-label="Quick actions" className="govuk-!-margin-bottom-8">
      <h2 className="govuk-heading-m">Quick actions</h2>
      <ul className="govuk-task-list">
        {[['/cases','Cases','Open the service desk'],['/customers','Customers','Search Customer 360'],
          ['/inbox','Inbox','Customer communications'],['/complaints','Complaints','Complaint handling']].map(([to,label,hint])=>
          <li key={to} className="govuk-task-list__item govuk-task-list__item--with-link">
            <div className="govuk-task-list__name-and-hint">
              <Link className="govuk-link govuk-task-list__link" to={to}>{label}</Link>
              <div className="govuk-task-list__hint">{hint}</div>
            </div>
          </li>)}
      </ul>
    </nav>

    {error&&data&&<div className="dashboard-inline-warning"><AlertTriangle size={16}/><span><strong>Refresh warning</strong>{error}</span></div>}
    {customError&&<div className="dashboard-inline-warning"><AlertTriangle size={16}/><span><strong>Dashboard card warning</strong>{customError}</span></div>}

    <section className="govuk-grid-row" aria-label="Key operational metrics">
      <Metric label="Open cases" value={m.openCases??'Restricted'} detail={m.myOpenCases==null?'Permission required':m.myOpenCases+' assigned to you'} icon={Briefcase} tone="blue" to="/cases"/>
      <Metric label="Urgent work" value={m.urgentCases??'Restricted'} detail={m.overdueCases==null?'Permission required':m.overdueCases+' overdue'} icon={AlertTriangle} tone={Number(m.urgentCases)>0?'orange':'green'} to="/cases"/>
      <Metric label="Active customers" value={m.activeCustomers??'Restricted'} detail="Customer360 records" icon={UsersRound} tone="teal" to="/customers"/>
      <Metric label="Open complaints" value={m.openComplaints??'Restricted'} detail="Complaint cases requiring action" icon={MessageSquareText} tone="purple" to="/complaints"/>
      <Metric label="Open leads" value={m.openLeads??'Restricted'} detail="Sales opportunities to progress" icon={UserRound} tone="cyan" to="/leads"/>
      <Metric label="Pipeline value" value={m.openDealValueMinor==null?'Restricted':money(m.openDealValueMinor)} detail={m.openDeals==null?'Permission required':m.openDeals+' open opportunities'} icon={Activity} tone="navy" to="/pipeline"/>
    </section>

    {data?.permissions?.cases&&<section className="dashboard-attention-strip">
      <div className="dashboard-attention-heading"><AlertTriangle size={15}/><strong>Operational attention</strong></div>
      <div className="dashboard-attention-items">{attention.map(item=><Link key={item.label} to={item.to} className={'dashboard-attention-item '+item.tone}><span>{item.label}</span><strong>{item.value??0}</strong></Link>)}</div>
    </section>}

    {(customCards.length>0||customLoading)?<section className="dashboard-custom-section">
      <header>
        <div><span>MY DASHBOARD</span><h2 className="govuk-heading-m">Added cards</h2><p className="govuk-body">Your personal live cards. Use the arrows to reorder them or remove cards you no longer need.</p></div>
        <button type="button" className="button secondary small govuk-button govuk-button--secondary" onClick={openLibrary}><Plus size={14}/>Add more cards</button>
      </header>
      {customLoading?<div className="dashboard-custom-loading"><div className="dashboard-spinner"/><span>Refreshing your cards…</span></div>:<div className="dashboard-custom-grid">
        {customCards.map((card,index)=><CustomDashboardCard key={card.id} card={card} index={index} total={customCards.length} onRemove={removeCard} onMove={moveCard}/>)}
      </div>}
    </section>:<button type="button" className="dashboard-add-cards-empty" onClick={openLibrary}>
      <span className="dashboard-add-cards-icon"><LayoutGrid size={20}/></span>
      <span><strong>Add cards to your dashboard</strong><small>Choose from more than 300 live case, workflow, deadline, channel and workload cards.</small></span>
      <Plus size={18}/>
    </button>}

    <div className="dashboard-command-grid">
      {data?.permissions?.cases&&<section className="dashboard-panel dashboard-my-work">
        <header><div><span>MY WORK</span><h2 className="govuk-heading-m">My case queue</h2><p className="govuk-body">Open cases assigned to you, ordered by urgency and target.</p></div><Link to="/cases?queue=assigned_to_me">View all <ArrowRight size={14}/></Link></header>
        <div className="dashboard-case-list">
          {data?.myCases?.length?data.myCases.map(row=><CaseRow key={row.id} row={row} onOpen={id=>navigate('/cases/'+encodeURIComponent(id))}/>):<div className="dashboard-panel-empty"><CheckCircle2 size={28}/><strong>No cases assigned to you</strong><span>Your personal queue is clear.</span></div>}
        </div>
      </section>}

      {data?.permissions?.cases&&<section className="dashboard-panel dashboard-workload">
        <header><div><span>CASE MANAGEMENT</span><h2 className="govuk-heading-m">Open workload</h2><p className="govuk-body">Current open work by case type.</p></div><Link to="/cases">Cases <ArrowRight size={14}/></Link></header>
        <div className="dashboard-workload-list">
          {workload.length?workload.map(item=>{
            const issue={issue_type:item.issue_type};
            const width=Math.max(6,Math.round((Number(item.count||0)/maxWorkload)*100));
            return <Link key={item.issue_type} to={'/cases?type='+encodeURIComponent(item.issue_type)} className="dashboard-workload-row">
              <div><CaseTypeBadge row={issue} compact/><strong>{Number(item.count||0)}</strong></div>
              <div className="dashboard-workload-track"><span style={{width:width+'%'}} className={'type-'+normaliseCaseIssueType(issue)}/></div>
            </Link>;
          }):<div className="dashboard-panel-empty small"><CheckCircle2 size={24}/><strong>No open case workload</strong><span>There are no visible open cases.</span></div>}
        </div>
      </section>}

      {data?.permissions?.cases&&<section className="dashboard-panel dashboard-recent-cases">
        <header><div><span>CASE MANAGEMENT</span><h2 className="govuk-heading-m">Cases needing attention</h2><p className="govuk-body">Priority, due date and recent activity considered.</p></div><Link to="/cases">All cases <ArrowRight size={14}/></Link></header>
        <div className="dashboard-case-list">
          {data?.recentCases?.length?data.recentCases.map(row=><CaseRow key={row.id} row={row} onOpen={id=>navigate('/cases/'+encodeURIComponent(id))}/>):<div className="dashboard-panel-empty"><CheckCircle2 size={28}/><strong>No open cases</strong><span>Nothing currently requires case-management attention.</span></div>}
        </div>
      </section>}

      {data?.permissions?.crm&&<section className="dashboard-panel dashboard-pipeline">
        <header><div><span>COMMERCIAL</span><h2 className="govuk-heading-m">Sales pipeline</h2><p className="govuk-body">Open opportunity value by active pipeline stage.</p></div><Link to="/pipeline">Open pipeline <ArrowRight size={14}/></Link></header>
        {data?.pipeline?.length?<div className="dashboard-pipeline-stages">{data.pipeline.map(stage=><div key={stage.id} className="dashboard-pipeline-stage"><div><span>{stage.name}</span><strong>{stage.deal_count}</strong></div><b>{money(stage.value_minor)}</b></div>)}</div>:<div className="dashboard-panel-empty small"><Briefcase size={24}/><strong>No pipeline stages</strong><span>No live default-pipeline data was returned.</span></div>}
      </section>}

      {data?.permissions?.crm&&<section className="dashboard-panel dashboard-leads">
        <header><div><span>SALES</span><h2 className="govuk-heading-m">Recent leads</h2><p className="govuk-body">Recently updated leads across the CRM.</p></div><Link to="/leads">All leads <ArrowRight size={14}/></Link></header>
        <div className="dashboard-lead-list">{data?.recentLeads?.length?data.recentLeads.map(lead=><button key={lead.id} type="button" onClick={()=>navigate('/leads')} className="dashboard-lead-row">
          <NameCard name={lead.display_name} subtitle={lead.email||lead.organisation_name||lead.lead_reference}/>
          <Status value={lead.status}/>
          <strong>{lead.value_minor==null?'—':money(lead.value_minor,lead.currency||'GBP')}</strong>
          <ChevronRight size={14}/>
        </button>):<div className="dashboard-panel-empty small"><UserRound size={24}/><strong>No recent leads</strong><span>New and updated leads will appear here.</span></div>}</div>
      </section>}

      {data?.permissions?.crm&&<section className="dashboard-panel dashboard-activity">
        <header><div><span>LIVE FEED</span><h2 className="govuk-heading-m">CRM activity</h2><p className="govuk-body">Latest visible operational events.</p></div></header>
        <div className="dashboard-activity-list">{data?.activity?.length?data.activity.map((item,index)=><article key={item.id||index}>
          <span className={'dashboard-activity-marker '+String(item.activity_type||'account').toLowerCase()}><Activity size={12}/></span>
          <div><strong>{item.summary||readable(item.activity_type)||'CRM activity'}</strong><small>{item.actor_name?'By '+item.actor_name:'System activity'}{item.source_reference&&<><br/><Reference>{item.source_reference}</Reference></>}</small></div>
          <time>{dateTime(item.occurred_at)}</time>
        </article>):<div className="dashboard-panel-empty small"><Activity size={24}/><strong>No recent activity</strong><span>Visible CRM events will appear here.</span></div>}</div>
      </section>}
    </div>

    {visibleWarnings.length>0&&<details className="dashboard-data-warnings">
      <summary><AlertTriangle size={14}/>Data notices <strong>{visibleWarnings.length}</strong></summary>
      <div>{visibleWarnings.map((warning,index)=><p className="govuk-body" key={index}>{warning}</p>)}</div>
    </details>}

    {libraryOpen&&<CardLibrary
      catalogue={catalogue}
      loading={libraryLoading}
      query={libraryQuery}
      setQuery={setLibraryQuery}
      group={libraryGroup}
      setGroup={setLibraryGroup}
      selectedIds={layout.card_ids||[]}
      onAdd={addCard}
      onRemove={removeCard}
      onClose={()=>setLibraryOpen(false)}
      saving={savingLayout}
    />}
  </div>;
}
